/**
 * SmartClientMatterSearch
 * Unified search across contacts + matters with Claude AI fallback.
 *
 * Props:
 *   label: string - field label
 *   placeholder: string
 *   onSelect: (result) => void - called when user picks a result
 *   initialValue: string - pre-fill the input
 *   showMatterRef: bool - whether to show matter reference in results
 *   className: string - wrapper className
 *   darkMode: bool - use dark teal theme (default true)
 *   mattersOnly: bool - search MATTERS only, never contacts (default false).
 *                       Use where the picked result must carry a
 *                       matter_reference (e.g. Invoice Tracker SP filing,
 *                       Brief 74) — a contact has none, so allowing one to be
 *                       picked leaves matter-keyed actions disabled.
 *   onQueryChange: (q) => void - optional, told every edit of the search text
 *                       so a caller can run a second search beside this one
 *                       (BM-H4029: the post screen also searches SharePoint).
 *
 * Recent + favourite matters (30 Sep 2026, Vanessa: "allow user to pick
 * matters from recent or select favourites for easy picking") — firm-wide,
 * live here rather than in any one caller. Focusing an empty field fetches
 * GET /matter-picks/quick (favourites + recently-picked matters) and shows
 * them instead of the normal (empty) results dropdown. Every matter row
 * everywhere (search results, favourites, recent) carries a star toggle
 * (POST/DELETE /matter-favourites). A real matter selection also fires a
 * best-effort POST /matter-picks -- fire-and-forget, never awaited, so a
 * slow or failed recording call can never delay or block onSelect.
 */
import { useState, useCallback, useRef, useEffect } from "react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Search, Loader2, User, Briefcase, X, Sparkles, Star,
} from "lucide-react";
import axios from "axios";
import { API } from "../config/api";

// Bonsai canonical matter-ref shapes seen in the wild:
//   SL.VEC.002.0060      (Vanessa 28 May 2026 11:48 BST repro — InvoiceRequest)
//   BL.VEC.006.0097
//   TLVEC.004.6403
//   VEC0089
//   ADL.006.0026
// Heuristic: starts with 2+ uppercase letters, then a mix of dots,
// slashes, hyphens, letters, digits — minimum 4 chars total.  Anchored
// to start/end so partial fragments don't trigger.  Case-insensitive
// because users paste in mixed case.  See test file for full coverage.
const MATTER_REF_RE = /^[A-Z]{2,}[A-Z0-9.\-/]{2,}$/i;

export default function SmartClientMatterSearch({
  label = "Client / Matter",
  placeholder = "Search clients or matters...",
  onSelect,
  initialValue = "",
  showMatterRef = true,
  className = "",
  darkMode = true,
  mattersOnly = false,
  onQueryChange,
}) {
  const [query, setQuery] = useState(initialValue);
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [aiTopMatch, setAiTopMatch] = useState(null);
  const [open, setOpen] = useState(false);
  const [quickPicks, setQuickPicks] = useState({ favourites: [], recent: [] });
  const [showQuickPicks, setShowQuickPicks] = useState(false);
  const [favouriteRefs, setFavouriteRefs] = useState(new Set());
  const timerRef = useRef(null);
  const wrapperRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
        setShowQuickPicks(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchQuickPicks = useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/matter-picks/quick`, { withCredentials: true });
      const data = resp.data || { favourites: [], recent: [] };
      setQuickPicks(data);
      setFavouriteRefs(new Set((data.favourites || []).map((f) => f.matter_ref)));
    } catch {
      setQuickPicks({ favourites: [], recent: [] });
    }
  }, []);

  const handleFocus = useCallback(() => {
    if (query.trim()) {
      if (results.length > 0) setOpen(true);
      return;
    }
    setShowQuickPicks(true);
    fetchQuickPicks();
  }, [query, results, fetchQuickPicks]);

  const handleToggleFavourite = useCallback(async (matterRef, matterLabel, e) => {
    e.stopPropagation();
    const isFav = favouriteRefs.has(matterRef);
    // Optimistic -- the star flips immediately, the request confirms it.
    setFavouriteRefs((prev) => {
      const next = new Set(prev);
      if (isFav) next.delete(matterRef); else next.add(matterRef);
      return next;
    });
    try {
      if (isFav) {
        await axios.delete(`${API}/matter-favourites/${matterRef}`, { withCredentials: true });
      } else {
        await axios.post(
          `${API}/matter-favourites`,
          { matter_ref: matterRef, label: matterLabel || matterRef },
          { withCredentials: true },
        );
      }
      fetchQuickPicks();
    } catch {
      // Revert the optimistic flip on failure.
      setFavouriteRefs((prev) => {
        const next = new Set(prev);
        if (isFav) next.add(matterRef); else next.delete(matterRef);
        return next;
      });
    }
  }, [favouriteRefs, fetchQuickPicks]);

  const doSearch = useCallback(async (q) => {
    if (q.length < 2) { setResults([]); setAiTopMatch(null); setOpen(false); return; }
    setSearching(true);
    try {
      const resp = await axios.get(`${API}/smart-search`, {
        params: { q, limit: 8, ...(mattersOnly ? { matters_only: true } : {}) },
        withCredentials: true,
      });
      const data = resp.data || {};
      setResults(data.results || []);
      setAiTopMatch(data.ai_top_match || null);
      setOpen((data.results || []).length > 0);
    } catch {
      setResults([]);
      setAiTopMatch(null);
    }
    setSearching(false);
  }, [mattersOnly]);

  const handleQueryChange = useCallback((e) => {
    const q = e.target.value;
    setQuery(q);
    if (onQueryChange) onQueryChange(q);
    setShowQuickPicks(false);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => doSearch(q), 300);

    // Eager propagation: if the typed value looks like a canonical
    // matter ref, write it back to parent state immediately. Without
    // this, parent formData.<ref-field> stays empty unless the user
    // clicks a dropdown result, and forms that validate the field
    // (InvoiceRequest, PaymentRequest, ConflictCheck, etc.) raise a
    // false "Matter reference is required" when the user types the ref
    // directly. See Vanessa 28 May 2026 SL.VEC.002.0060 repro.
    //
    // Empty input also propagates so stale ref doesn't linger in
    // parent state during a retype — mirrors handleClear's behaviour
    // without tearing down the dropdown.
    const trimmed = q.trim();
    if (trimmed && MATTER_REF_RE.test(trimmed)) {
      if (onSelect) onSelect({
        id: null,
        type: "typed",
        label: trimmed,
        matter_reference: trimmed,
        client_name: "",
        matter_title: "",
      });
    } else if (!trimmed || mattersOnly) {
      // BM-H3459: in mattersOnly, any edit that isn't a matter ref drops
      // the stored pick, so the field can never show one name while the
      // caller holds a different matter (or holds one the text no longer
      // describes). The user re-picks from the dropdown.
      if (onSelect) onSelect({
        id: null,
        type: "typed",
        label: "",
        matter_reference: "",
        client_name: "",
        matter_title: "",
      });
    }
  }, [doSearch, onSelect, mattersOnly, onQueryChange]);

  const handleSelect = useCallback((r) => {
    setQuery(r.label || r.client_name || "");
    setResults([]);
    setOpen(false);
    setShowQuickPicks(false);
    setAiTopMatch(null);
    if (onSelect) onSelect(r);
    // Best-effort, fire-and-forget: recording the pick must never delay
    // or block the selection itself (see this component's own docstring).
    if (r.matter_reference) {
      axios.post(
        `${API}/matter-picks`,
        { matter_ref: r.matter_reference, label: r.label || r.client_name || r.matter_reference },
        { withCredentials: true },
      ).catch(() => {});
    }
  }, [onSelect]);

  const handleUseAsTyped = useCallback(() => {
    setResults([]);
    setOpen(false);
    setShowQuickPicks(false);
    setAiTopMatch(null);
    if (onSelect) onSelect({ id: null, type: "typed", label: query, client_name: query });
  }, [onSelect, query]);

  const handleClear = useCallback(() => {
    setQuery("");
    setResults([]);
    setOpen(false);
    setShowQuickPicks(false);
    setAiTopMatch(null);
    if (onQueryChange) onQueryChange("");
    if (onSelect) onSelect({ id: null, type: "typed", label: "", client_name: "" });
  }, [onSelect, onQueryChange]);

  // Group results
  const contacts = results.filter(r => r.type === "contact");
  // BM-H3459: near-miss hits used to arrive as kind="matter" with no type,
  // and were silently dropped here. The backend now sets type; still accept
  // kind so a result the server found can never vanish from the list.
  const matters = results.filter(r => r.type === "matter" || (!r.type && r.kind === "matter"));

  const inputClasses = darkMode
    ? "pl-8 bg-navy-hover border-navy-border text-white placeholder:text-txt-muted focus:border-teal focus:ring-teal"
    : "pl-8 border-gray-200 focus:border-teal focus:ring-teal";

  const dropdownClasses = darkMode
    ? "border border-navy-border rounded-md bg-navy-card shadow-xl"
    : "border border-gray-200 rounded-md bg-white shadow-lg";

  const itemHoverClasses = darkMode
    ? "hover:bg-teal-dim/30"
    : "hover:bg-gray-50";

  const labelTextClasses = darkMode ? "text-sm text-white font-medium truncate" : "text-sm text-gray-900 font-medium truncate";
  const sublabelTextClasses = darkMode ? "text-xs text-slate-400 truncate" : "text-xs text-gray-500 truncate";
  const sectionHeaderClasses = darkMode
    ? "px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500 bg-navy"
    : "px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-400 bg-gray-50";

  return (
    <div className={className} ref={wrapperRef}>
      {label && <Label className={darkMode ? "text-sm font-medium text-teal" : "text-sm font-medium"}>{label}</Label>}

      <div className="relative mt-1">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 z-10" />
        <Input
          value={query}
          onChange={handleQueryChange}
          onFocus={handleFocus}
          placeholder={placeholder}
          className={inputClasses}
        />
        {searching && <Loader2 className="absolute right-8 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-blue-400 animate-spin" />}
        {query && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200 p-0.5"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}

        {showQuickPicks && !query.trim() && (quickPicks.favourites.length > 0 || quickPicks.recent.length > 0) && (
          <div className={`absolute z-30 top-full mt-1 left-0 right-0 max-h-64 overflow-y-auto ${dropdownClasses}`}>
            {quickPicks.favourites.length > 0 && (
              <>
                <div className={sectionHeaderClasses}>
                  <Star className="w-3 h-3 inline mr-1 -mt-0.5" />Favourites
                </div>
                {quickPicks.favourites.map((f) => (
                  <div
                    key={`fav-${f.matter_ref}`}
                    onClick={() => handleSelect({ type: "matter", label: f.label, matter_reference: f.matter_ref, client_name: f.label })}
                    className={`px-3 py-2 cursor-pointer border-b border-navy-border/30 last:border-0 flex items-start justify-between gap-2 ${itemHoverClasses}`}
                  >
                    <div className="min-w-0 flex-1">
                      <span className={labelTextClasses}>{f.label}</span>
                      {showMatterRef && <div className={sublabelTextClasses}>{f.matter_ref}</div>}
                    </div>
                    <button
                      type="button"
                      onClick={(e) => handleToggleFavourite(f.matter_ref, f.label, e)}
                      className="shrink-0 text-amber-400 hover:text-slate-400 p-0.5"
                      title="Remove from favourites"
                      data-testid={`matter-star-${f.matter_ref}`}
                    >
                      <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                    </button>
                  </div>
                ))}
              </>
            )}
            {quickPicks.recent.length > 0 && (
              <>
                <div className={sectionHeaderClasses}>
                  <Briefcase className="w-3 h-3 inline mr-1 -mt-0.5" />Recent
                </div>
                {quickPicks.recent.map((r) => (
                  <div
                    key={`recent-${r.matter_ref}`}
                    onClick={() => handleSelect({ type: "matter", label: r.label, matter_reference: r.matter_ref, client_name: r.label })}
                    className={`px-3 py-2 cursor-pointer border-b border-navy-border/30 last:border-0 flex items-start justify-between gap-2 ${itemHoverClasses}`}
                  >
                    <div className="min-w-0 flex-1">
                      <span className={labelTextClasses}>{r.label}</span>
                      {showMatterRef && <div className={sublabelTextClasses}>{r.matter_ref}</div>}
                    </div>
                    <button
                      type="button"
                      onClick={(e) => handleToggleFavourite(r.matter_ref, r.label, e)}
                      className="shrink-0 text-slate-500 hover:text-amber-400 p-0.5"
                      title="Add to favourites"
                      data-testid={`matter-star-${r.matter_ref}`}
                    >
                      <Star className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </>
            )}
          </div>
        )}

        {open && results.length > 0 && (
          <div className={`absolute z-30 top-full mt-1 left-0 right-0 max-h-64 overflow-y-auto ${dropdownClasses}`}>
            {/* Contacts section */}
            {contacts.length > 0 && (
              <>
                <div className={sectionHeaderClasses}>
                  <User className="w-3 h-3 inline mr-1 -mt-0.5" />Contacts
                </div>
                {contacts.map((r, i) => {
                  const isAiPick = aiTopMatch && r.id === aiTopMatch;
                  return (
                    <div
                      key={`c-${i}`}
                      onClick={() => handleSelect(r)}
                      className={`px-3 py-2 cursor-pointer border-b border-navy-border/30 last:border-0 ${itemHoverClasses} ${isAiPick ? (darkMode ? "bg-teal-dim/20 border-l-2 border-l-teal" : "bg-teal-dim border-l-2 border-l-teal") : ""}`}
                    >
                      <div className="flex items-center gap-1.5">
                        {isAiPick && <Sparkles className="w-3 h-3 text-teal shrink-0" />}
                        <span className={labelTextClasses}>{r.label}</span>
                      </div>
                      {(r.email || r.phone) && (
                        <div className={sublabelTextClasses}>
                          {[r.email, r.phone].filter(Boolean).join(" \u00b7 ")}
                        </div>
                      )}
                    </div>
                  );
                })}
              </>
            )}

            {/* Matters section */}
            {matters.length > 0 && (
              <>
                <div className={sectionHeaderClasses}>
                  <Briefcase className="w-3 h-3 inline mr-1 -mt-0.5" />Matters
                </div>
                {matters.map((r, i) => {
                  const isAiPick = aiTopMatch && r.id === aiTopMatch;
                  // 29 Sep 2026: matters_only can still surface a
                  // SharePoint-only "pre-CM" match (a folder with no Mongo
                  // matter row) whose matter_reference is empty -- every
                  // mattersOnly caller needs a real one (see this
                  // component's own docstring), so selecting it would
                  // silently fail the caller's own "matter required"
                  // check. Mute it and refuse the click here instead of
                  // letting it look identical to a real, pickable result.
                  const noRef = mattersOnly && !r.matter_reference;
                  const isFav = r.matter_reference && favouriteRefs.has(r.matter_reference);
                  return (
                    <div
                      key={`m-${i}`}
                      onClick={noRef ? undefined : () => handleSelect(r)}
                      data-testid={noRef ? "matter-result-no-ref" : undefined}
                      className={`px-3 py-2 border-b border-navy-border/30 last:border-0 flex items-start justify-between gap-2 ${
                        noRef
                          ? "opacity-50 cursor-not-allowed"
                          : `cursor-pointer ${itemHoverClasses} ${isAiPick ? (darkMode ? "bg-teal-dim/20 border-l-2 border-l-teal" : "bg-teal-dim border-l-2 border-l-teal") : ""}`
                      }`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          {isAiPick && <Sparkles className="w-3 h-3 text-teal shrink-0" />}
                          <span className={labelTextClasses}>{r.label}</span>
                        </div>
                        {noRef ? (
                          <div className={sublabelTextClasses}>No matter reference yet — can't be used here</div>
                        ) : (
                          <>
                            {showMatterRef && r.sublabel && (
                              <div className={sublabelTextClasses}>{r.sublabel}</div>
                            )}
                            {r.fee_earner && (
                              <div className={sublabelTextClasses}>FE: {r.fee_earner}</div>
                            )}
                          </>
                        )}
                      </div>
                      {!noRef && (
                        <button
                          type="button"
                          onClick={(e) => handleToggleFavourite(r.matter_reference, r.label, e)}
                          className="shrink-0 text-slate-500 hover:text-amber-400 p-0.5"
                          title={isFav ? "Remove from favourites" : "Add to favourites"}
                          data-testid={`matter-star-${r.matter_reference}`}
                        >
                          <Star className={`w-3.5 h-3.5 ${isFav ? "fill-amber-400 text-amber-400" : ""}`} />
                        </button>
                      )}
                    </div>
                  );
                })}
              </>
            )}

            {/* Use as typed -- never in mattersOnly (BM-H3459): free text
                carries no matter_reference, so every mattersOnly caller
                rejects it ("Pick a matter first") after it looked accepted. */}
            {!mattersOnly && (
            <div
              onClick={handleUseAsTyped}
              className={`px-3 py-2 cursor-pointer text-center ${itemHoverClasses} border-t border-navy-border/30`}
            >
              <span className={darkMode ? "text-xs text-slate-500" : "text-xs text-gray-400"}>
                Use "{query}" as typed
              </span>
            </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
